import React, { useState } from 'react';
import styled from 'styled-components';
import Image from '../../atoms/Image';
import { Drug, DrugSearchResult } from '../../../common/models/types';
import { getDrugImageUrl, getDrugProductName, getDrugChart, getDrugPrint } from '../../../utils/drugImageUtils';

const DrugImageContainer = styled.div<{ clickable?: boolean; showCard?: boolean }>`
  display: grid;
  /* minmax(0, 1fr): 1fr 트랙의 기본 min-width(auto=콘텐츠 최소폭)를 0으로 풀어
     좁은 패널에서 정보 영역이 콘텐츠보다 작게 줄어들 수 있게 한다.
     (이게 없으면 긴 의약품명이 트랙 폭을 키워 가로 스크롤이 생김) */
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  margin: 8px 0;
  max-width: 100%;
  align-items: start;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  /* 카드 스타일 (pillsData가 있을 때) */
  ${({ showCard }) => showCard && `
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
  `}

  /* 좁은 화면에서는 세로 배치 */
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
    justify-items: start;
  }

  /* 인쇄 시 환자용으로 크게 표시 */
  @media print {
    gap: 14px;
    padding: 16px;
    margin: 12px 0;
    /* 약 카드가 페이지 경계에서 쪼개지지 않게 한다(D-17). */
    break-inside: avoid;
    page-break-inside: avoid;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ImagePlaceholder = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
  height: ${({ width }) => Math.round(width * 426 / 780)}px;
  background: #f5f5f5;
  border: 1px dashed #ccc;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '이미지 없음';
    font-size: 9px;
    color: #aaa;
    text-align: center;
    line-height: 1.2;
  }

  @media print {
    width: ${({ width }) => Math.round(width * 1.3)}px;
    height: ${({ width }) => Math.round(width * 1.3 * 426 / 780)}px;

    &::after {
      font-size: 11px;
    }
  }
`;

const ProductName = styled.span`
  font-size: 12px;
  color: #666;
  word-break: keep-all;
  overflow-wrap: break-word;
  line-height: 1.4;

  /* 최대 2줄로 제한 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  /* 인쇄 시 크게 */
  @media print {
    font-size: 14px;
    font-weight: 600;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* grid 트랙이 줄어들 때 자식(긴 의약품명 등)이 트랙을 넘지 않도록 */
  min-width: 0;
`;

const ChartInfo = styled.span`
  font-size: 11px;
  color: #888;
  line-height: 1.3;

  /* 인쇄 시 크게 */
  @media print {
    font-size: 13px;
    color: #555;
  }
`;

const PrintInfo = styled.span`
  font-size: 11px;
  color: #888;
  line-height: 1.3;

  &::before {
    content: '각인: ';
    color: #999;
  }

  /* 인쇄 시 크게 */
  @media print {
    font-size: 13px;
    color: #555;

    &::before {
      color: #666;
    }
  }
`;

interface DrugImageProps {
  /**
   * 약품 품목코드
   */
  itemCode: string;

  /**
   * 약품 정보 배열 (제품명 조회용)
   */
  drugsData?: Drug[];

  /**
   * 약품 식별정보 배열 (성상/각인 조회용)
   */
  pillsData?: DrugSearchResult[];

  /**
   * 이미지 너비 (픽셀)
   * @default 100
   */
  width?: number;

  /**
   * 호버 줌 활성화 여부
   * @default true
   */
  enableHoverZoom?: boolean;

  /**
   * 줌 배율
   * @default 4
   */
  zoomScale?: number;

  /**
   * 클릭 이벤트 핸들러
   */
  onClick?: (itemCode: string) => void;
}

/**
 * 약품 이미지 및 제품명을 표시하는 컴포넌트
 *
 * @example
 * ```tsx
 * <DrugImage itemCode="200001234" drugsData={drugs} />
 * ```
 */
const DrugImage: React.FC<DrugImageProps> = ({
  itemCode,
  drugsData,
  pillsData,
  width = 100,
  enableHoverZoom = true,
  zoomScale = 4,
  onClick,
}) => {
  const [imageError, setImageError] = useState(false);
  const imageUrl = getDrugImageUrl(itemCode);
  const productName = getDrugProductName(itemCode, drugsData);
  const chart = getDrugChart(itemCode, pillsData);
  const print = getDrugPrint(itemCode, pillsData);
  // pillsData가 전달되면 항상 카드 형태로 표시 (식별정보 유무와 관계없이)
  const showCard = !!pillsData;

  return (
    <DrugImageContainer
      data-drug-container="true"
      data-itemcode={itemCode}
      clickable={!!onClick}
      showCard={showCard}
      onClick={onClick ? () => onClick(itemCode) : undefined}
    >
      <ImageWrapper>
        {imageError ? (
          <ImagePlaceholder width={width} data-drug-placeholder="true" />
        ) : (
          <Image
            src={imageUrl}
            alt="약품이미지"
            width={width}
            objectFit="contain"
            enableHoverZoom={enableHoverZoom}
            zoomScale={zoomScale}
            showOnError={false}
            onError={() => setImageError(true)}
          />
        )}
      </ImageWrapper>
      <InfoWrapper>
        {productName && <ProductName data-drug-name="true">{productName}</ProductName>}
        {chart && <ChartInfo data-drug-chart="true">{chart}</ChartInfo>}
        {print && <PrintInfo data-drug-print="true">{print}</PrintInfo>}
      </InfoWrapper>
    </DrugImageContainer>
  );
};

export default DrugImage;
